import React, { useState, useEffect } from 'react';
import { 
  Firestore,
  collection,
  query,
  where,
  getDocs
} from 'firebase/firestore';
import { Functions, httpsCallable } from 'firebase/functions';
import { getLocationById } from './config/locationConfig';

interface Store {
  id?: string;
  name: string;
  // Test environment Stripe details
  stripeTestAccountId?: string;
  stripeTestAccountName?: string;
  stripeTestConnected: boolean;
  stripeTestConnectUrl?: string;
  stripeTestConnectUrlExpires?: Date | any;
  stripeTestOnboardingStarted?: Date | any;
  // Live environment Stripe details
  stripeLiveAccountId?: string;
  stripeLiveAccountName?: string;
  stripeLiveConnected: boolean;
  stripeLiveConnectUrl?: string;
  stripeLiveConnectUrlExpires?: Date | any;
  stripeLiveOnboardingStarted?: Date | any;
  // Legacy fields for backward compatibility
  stripeAccountId?: string;
  stripeAccountName?: string;
  stripeConnected: boolean;
  stripeConnectUrl?: string;
  stripeConnectUrlExpires?: Date | any;
  stripeOnboardingStarted?: Date | any;
  stripeOrganization?: string;
  stripeLocationId?: string;
  createdAt: Date | any;
  updatedAt: Date | any;
}

interface ExistingStripeAccount {
  accountId: string;
  accountName: string;
  storeName: string;
  storeId: string;
  organizationName?: string;
  environment: 'test' | 'live';
}

interface StripeConnectionModalProps {
  store: Store;
  appId: string;
  locationId: string;
  environment: 'test' | 'live';
  db: Firestore;
  functions: Functions;
  onSuccess: (message: string) => void;
  onError: (error: string) => void;
  onCancel: () => void;
  setLoading: (loading: boolean) => void;
}

const StripeConnectionModal: React.FC<StripeConnectionModalProps> = ({
  store,
  appId,
  locationId,
  environment,
  db,
  functions,
  onSuccess,
  onError,
  onCancel,
  setLoading
}) => {
  const [connectionType, setConnectionType] = useState<'new' | 'existing' | ''>('');
  const [newAccountName, setNewAccountName] = useState<string>('');
  const [selectedExistingAccount, setSelectedExistingAccount] = useState<string>('');
  const [existingAccounts, setExistingAccounts] = useState<ExistingStripeAccount[]>([]);
  const [showConfirmation, setShowConfirmation] = useState<boolean>(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isValidating, setIsValidating] = useState<boolean>(false);
  const [generatedConnectUrl, setGeneratedConnectUrl] = useState<string>('');
  const [showConnectUrl, setShowConnectUrl] = useState<boolean>(false);

  // Cloud function references
  const createStripeConnectAccountFunc = httpsCallable(functions, 'createStripeConnectAccount');
  const validateStripeAccountFunc = httpsCallable(functions, 'validateStripeAccount');
  const connectExistingStripeAccountFunc = httpsCallable(functions, 'connectExistingStripeAccount');
  const regenerateStripeConnectLinkFunc = httpsCallable(functions, 'regenerateStripeConnectLink');

  // Get current location config
  const currentLocation = getLocationById(locationId);

  // Get environment-specific store data
  const getEnvironmentStoreData = () => {
    if (environment === 'test') {
      return {
        accountId: store.stripeTestAccountId,
        accountName: store.stripeTestAccountName,
        connected: store.stripeTestConnected,
        connectUrl: store.stripeTestConnectUrl,
        connectUrlExpires: store.stripeTestConnectUrlExpires,
        onboardingStarted: store.stripeTestOnboardingStarted
      };
    } else {
      return {
        accountId: store.stripeLiveAccountId,
        accountName: store.stripeLiveAccountName,
        connected: store.stripeLiveConnected,
        connectUrl: store.stripeLiveConnectUrl,
        connectUrlExpires: store.stripeLiveConnectUrlExpires,
        onboardingStarted: store.stripeLiveOnboardingStarted
      };
    }
  };

  const envData = getEnvironmentStoreData();

  // Load existing Stripe accounts for this app and organization
  useEffect(() => {
    const loadExistingAccounts = async () => {
      try {
        const q = query(
          collection(db, 'stores'),
          where('appId', '==', appId)
        );
        
        const querySnapshot = await getDocs(q);
        const accounts: ExistingStripeAccount[] = [];
        
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          if (doc.id !== store.id) {
            // Filter by same organization to ensure compatibility
            if (data.stripeOrganization === currentLocation.organizationName) {
              // Add test accounts
              if (environment === 'test' && data.stripeTestAccountId && data.stripeTestAccountName && data.stripeTestConnected) {
                accounts.push({
                  accountId: data.stripeTestAccountId,
                  accountName: data.stripeTestAccountName,
                  storeName: data.name,
                  storeId: doc.id,
                  organizationName: data.stripeOrganization,
                  environment: 'test'
                });
              }
              // Add live accounts
              if (environment === 'live' && data.stripeLiveAccountId && data.stripeLiveAccountName && data.stripeLiveConnected) {
                accounts.push({
                  accountId: data.stripeLiveAccountId,
                  accountName: data.stripeLiveAccountName,
                  storeName: data.name,
                  storeId: doc.id,
                  organizationName: data.stripeOrganization,
                  environment: 'live'
                });
              }
            }
          }
        });

        // Remove duplicates (same Stripe account used by multiple stores)
        const uniqueAccounts = accounts.filter((account, index, self) => 
          index === self.findIndex(a => a.accountId === account.accountId && a.environment === account.environment)
        );

        setExistingAccounts(uniqueAccounts);
      } catch (err) {
        console.error('Error loading existing accounts:', err);
      }
    };

    loadExistingAccounts();
  }, [db, appId, store.id, currentLocation.organizationName, environment]);

  // Generate account name from store name
  useEffect(() => {
    if (connectionType === 'new' && !newAccountName) {
      setNewAccountName(`${store.name} (${environment.toUpperCase()})`);
    }
  }, [connectionType, store.name, newAccountName, environment]);

  // Validate form
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!connectionType) {
      newErrors.connectionType = 'Please select a connection type';
    } else if (connectionType === 'new') {
      if (!newAccountName.trim()) {
        newErrors.newAccountName = 'Account name is required';
      } else if (newAccountName.trim().length < 2) {
        newErrors.newAccountName = 'Account name must be at least 2 characters';
      } else if (newAccountName.trim().length > 50) {
        newErrors.newAccountName = 'Account name must be less than 50 characters';
      }
    } else if (connectionType === 'existing') {
      if (!selectedExistingAccount) {
        newErrors.selectedExistingAccount = 'Please select an existing account';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle new Stripe account creation
  const handleCreateNewAccount = async () => {
    if (!validateForm()) return;

    setLoading(true);
    try {
      const result = await createStripeConnectAccountFunc({
        appId: appId,
        storeId: store.id,
        accountName: newAccountName.trim(),
        environment: environment,
        returnUrl: `${window.location.origin}`,
        refreshUrl: `${window.location.origin}`
      });

      // @ts-ignore
      if (result.data && result.data.success) {
        // @ts-ignore
        const connectUrl = result.data.connectUrl;
        // @ts-ignore
        const organizationName = result.data.organizationName;
        
        setGeneratedConnectUrl(connectUrl);
        setShowConnectUrl(true);
        // @ts-ignore
        onSuccess(`${result.data.message} Using ${organizationName} Stripe organization for ${environment} environment.`);
      } else {
        // @ts-ignore
        onError(result.data?.message || `Failed to create ${environment} Stripe Connect account.`);
      }
    } catch (err: any) {
      console.error('Error creating Stripe Connect account:', err);
      onError(err.message || `Failed to start ${environment} Stripe Connect process.`);
    } finally {
      setLoading(false);
    }
  };

  // Handle existing account selection
  const handleSelectExistingAccount = () => {
    if (!validateForm()) return;

    const selectedAccount = existingAccounts.find(acc => acc.accountId === selectedExistingAccount);
    if (!selectedAccount) {
      onError('Selected account not found');
      return;
    }

    setShowConfirmation(true);
  };

  // Validate existing Stripe account
  const validateExistingAccount = async (accountId: string): Promise<boolean> => {
    try {
      setIsValidating(true);
      const result = await validateStripeAccountFunc({ 
        accountId,
        locationId,
        environment
      });
      
      // @ts-ignore
      if (result.data && result.data.valid) {
        return true;
      } else {
        // @ts-ignore
        onError(result.data?.message || `The selected ${environment} Stripe account has issues and cannot be used.`);
        return false;
      }
    } catch (err: any) {
      console.error('Error validating Stripe account:', err);
      onError(`Failed to validate ${environment} Stripe account. Please try again.`);
      return false;
    } finally {
      setIsValidating(false);
    }
  };

  // Check if store already has a connect URL for this environment
  const hasExistingConnectUrl = () => {
    if (!envData.connectUrl || envData.connected) return false;
    
    // Check if URL is expired
    if (envData.connectUrlExpires) {
      const expiryDate = envData.connectUrlExpires instanceof Date 
        ? envData.connectUrlExpires 
        : envData.connectUrlExpires.toDate();
      return new Date() < expiryDate;
    }
    
    return true; // Assume valid if no expiry date
  };

  // Regenerate expired connect URL
  const handleRegenerateLink = async () => {
    setLoading(true);
    try {
      const result = await regenerateStripeConnectLinkFunc({
        storeId: store.id,
        environment: environment,
        returnUrl: `${window.location.origin}`,
        refreshUrl: `${window.location.origin}`
      });

      // @ts-ignore
      if (result.data && result.data.success) {
        // @ts-ignore
        const connectUrl = result.data.connectUrl;
        setGeneratedConnectUrl(connectUrl);
        setShowConnectUrl(true);
        // @ts-ignore
        onSuccess(result.data.message || `New ${environment} Stripe Connect link generated successfully!`);
      } else {
        // @ts-ignore
        onError(result.data?.message || `Failed to regenerate ${environment} Stripe Connect link.`);
      }
    } catch (err: any) {
      console.error('Error regenerating Stripe Connect link:', err);
      onError(err.message || `Failed to regenerate ${environment} Stripe Connect link.`);
    } finally {
      setLoading(false);
    }
  };

  // Copy URL to clipboard
  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      onSuccess('Link copied to clipboard!');
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      onSuccess('Link copied to clipboard!');
    }
  };

  const handleConnectExistingAccount = async () => {
    const selectedAccount = existingAccounts.find(acc => acc.accountId === selectedExistingAccount);
    if (!selectedAccount) return;

    // Validate the account first
    const isValid = await validateExistingAccount(selectedAccount.accountId);
    if (!isValid) return;

    setLoading(true);
    try {
      const result = await connectExistingStripeAccountFunc({
        storeId: store.id,
        stripeAccountId: selectedAccount.accountId,
        stripeAccountName: selectedAccount.accountName,
        environment: environment,
        locationId: locationId
      });

      // @ts-ignore
      if (result.data && result.data.success) {
        // @ts-ignore
        onSuccess(result.data.message || `Store "${store.name}" has been connected to the existing ${environment} Stripe account "${selectedAccount.accountName}".`);
        onCancel(); // Close modal
      } else {
        // @ts-ignore
        onError(result.data?.message || `Failed to connect existing ${environment} Stripe account.`);
      }
    } catch (err: any) {
      console.error('Error connecting existing Stripe account:', err);
      onError(err.message || `Failed to connect existing ${environment} Stripe account.`);
    } finally {
      setLoading(false);
    }
  };

  const selectedAccount = existingAccounts.find(acc => acc.accountId === selectedExistingAccount);

  return (
    <div className="modal-overlay">
      <div className="modal-container stripe-connection-modal">
        <div className="modal-content">
          <div className="modal-header">
            <h3>Connect {environment.toUpperCase()} Stripe Account</h3>
            <button 
              type="button" 
              className="modal-close"
              onClick={onCancel}
            >
              ×
            </button>
          </div>
          
          <div className="modal-body">
            <div className="store-info">
              <h4>Store: {store.name}</h4>
              <div className="environment-info">
                <strong>Environment:</strong> {environment.toUpperCase()}
                <span className={`environment-badge ${environment}`}>
                  {environment === 'test' ? '🧪 Test Mode' : '🔴 Live Mode'}
                </span>
              </div>
              <div className="location-info">
                <strong>Organization:</strong> {currentLocation.organizationName} ({currentLocation.locationName})
              </div>
              <p>Choose how you want to connect a {environment} Stripe account for payment processing.</p>
            </div>

            {!showConfirmation && !showConnectUrl ? (
              <div className="connection-options">
                {/* Check if store already has a pending connect URL */}
                {hasExistingConnectUrl() && (
                  <div className="existing-connect-url">
                    <h5>Existing {environment.toUpperCase()} Stripe Connect Link</h5>
                    <div className="connect-url-section">
                      <p>This store already has a pending {environment} Stripe Connect link:</p>
                      <div className="url-display">
                        <input
                          type="text"
                          value={envData.connectUrl || ''}
                          readOnly
                          className="url-input"
                        />
                        <button
                          type="button"
                          className="copy-btn"
                          onClick={() => copyToClipboard(envData.connectUrl || '')}
                        >
                          Copy
                        </button>
                      </div>
                      <div className="url-actions">
                        <button
                          type="button"
                          className="regenerate-btn"
                          onClick={handleRegenerateLink}
                        >
                          Generate New Link
                        </button>
                        <button
                          type="button"
                          className="open-link-btn"
                          onClick={() => window.open(envData.connectUrl, '_blank')}
                        >
                          Open Link
                        </button>
                      </div>
                      {envData.connectUrlExpires && (
                        <small className="expiry-info">
                          Expires: {new Date(envData.connectUrlExpires instanceof Date 
                            ? envData.connectUrlExpires 
                            : envData.connectUrlExpires.toDate()
                          ).toLocaleString()}
                        </small>
                      )}
                    </div>
                  </div>
                )}

                <div className="connection-type-selection">
                  <h5>Select Connection Type</h5>
                  
                  <div className="radio-group">
                    {existingAccounts.length > 0 && (
                      <label className="radio-option">
                        <input
                          type="radio"
                          name="connectionType"
                          value="existing"
                          checked={connectionType === 'existing'}
                          onChange={(e) => setConnectionType(e.target.value as 'existing')}
                        />
                        <div className="radio-content">
                          <h6>Use Existing {environment.toUpperCase()} Stripe Account</h6>
                          <p>Connect to a {environment} Stripe account already used by another store in this app (same organization: {currentLocation.organizationName}).</p>
                        </div>
                      </label>
                    )}
                    
                    <label className="radio-option">
                      <input
                        type="radio"
                        name="connectionType"
                        value="new"
                        checked={connectionType === 'new'}
                        onChange={(e) => setConnectionType(e.target.value as 'new')}
                      />
                      <div className="radio-content">
                        <h6>Connect to a New {environment.toUpperCase()} Stripe Account</h6>
                        <p>Create or connect to a different {environment} Stripe account for this store (organization: {currentLocation.organizationName}).</p>
                      </div>
                    </label>
                  </div>

                  {errors.connectionType && (
                    <span className="error-text">{errors.connectionType}</span>
                  )}
                </div>

                {connectionType === 'existing' && existingAccounts.length > 0 && (
                  <div className="existing-account-selection">
                    <h5>Select Existing {environment.toUpperCase()} Account ({currentLocation.organizationName})</h5>
                    <select
                      value={selectedExistingAccount}
                      onChange={(e) => setSelectedExistingAccount(e.target.value)}
                      className={errors.selectedExistingAccount ? 'error' : ''}
                    >
                      <option value="">Choose an account...</option>
                      {existingAccounts.map((account) => (
                        <option key={account.accountId} value={account.accountId}>
                          {account.accountName} (used by {account.storeName})
                        </option>
                      ))}
                    </select>
                    {errors.selectedExistingAccount && (
                      <span className="error-text">{errors.selectedExistingAccount}</span>
                    )}
                    <small className="form-help-text">
                      Only showing {environment} accounts from the same organization ({currentLocation.organizationName}) for compatibility.
                    </small>
                  </div>
                )}

                {connectionType === 'new' && (
                  <div className="new-account-setup">
                    <h5>Account Name</h5>
                    <input
                      type="text"
                      value={newAccountName}
                      onChange={(e) => setNewAccountName(e.target.value)}
                      placeholder={`Enter a name for this ${environment} Stripe account`}
                      className={errors.newAccountName ? 'error' : ''}
                    />
                    {errors.newAccountName && (
                      <span className="error-text">{errors.newAccountName}</span>
                    )}
                    <small className="form-help-text">
                      This name will help you identify this {environment} Stripe account when managing multiple stores. 
                      This will be created under the {currentLocation.organizationName} organization.
                    </small>
                  </div>
                )}
              </div>
            ) : showConnectUrl ? (
              <div className="connect-url-generated">
                <div className="success-message">
                  <h4>✅ {environment.toUpperCase()} Stripe Connect Link Generated</h4>
                  <p>Use this link to complete your {environment} Stripe account setup:</p>
                </div>
                
                <div className="url-display">
                  <input
                    type="text"
                    value={generatedConnectUrl}
                    readOnly
                    className="url-input"
                  />
                  <button
                    type="button"
                    className="copy-btn"
                    onClick={() => copyToClipboard(generatedConnectUrl)}
                  >
                    Copy
                  </button>
                </div>
                
                <div className="url-actions">
                  <button
                    type="button"
                    className="open-link-btn primary"
                    onClick={() => window.open(generatedConnectUrl, '_blank')}
                  >
                    Open {environment.toUpperCase()} Stripe Connect
                  </button>
                </div>
                
                <div className="connect-instructions">
                  <h5>Next Steps:</h5>
                  <ol>
                    <li>Click "Open {environment.toUpperCase()} Stripe Connect" or copy the link</li>
                    <li>Complete the Stripe onboarding process for {environment} environment</li>
                    <li>Return to this app when finished</li>
                    <li>Your store will be automatically activated for {environment} payments</li>
                  </ol>
                </div>
              </div>
            ) : (
              <div className="confirmation-section">
                <div className="confirmation-header">
                  <div className="confirmation-icon">⚠️</div>
                  <h4>Confirm {environment.toUpperCase()} Account Connection</h4>
                </div>
                
                <div className="confirmation-details">
                  <p>You are about to connect <strong>"{store.name}"</strong> to the existing {environment} Stripe account:</p>
                  
                  <div className="account-details">
                    <div className="detail-item">
                      <label>Account Name:</label>
                      <span>{selectedAccount?.accountName}</span>
                    </div>
                    <div className="detail-item">
                      <label>Environment:</label>
                      <span>{environment.toUpperCase()}</span>
                    </div>
                    <div className="detail-item">
                      <label>Currently used by:</label>
                      <span>{selectedAccount?.storeName}</span>
                    </div>
                    <div className="detail-item">
                      <label>Organization:</label>
                      <span>{selectedAccount?.organizationName || currentLocation.organizationName}</span>
                    </div>
                  </div>

                  <div className="warning-message">
                    <p><strong>Important:</strong> Both stores will receive {environment} payments to the same bank account. Make sure this is what you want.</p>
                  </div>
                </div>
              </div>
            )}
          </div>
          
          <div className="modal-footer">
            {!showConfirmation && !showConnectUrl ? (
              <>
                {connectionType === 'new' && (
                  <button
                    type="button"
                    className="submit-btn"
                    onClick={handleCreateNewAccount}
                    disabled={!connectionType || !!Object.keys(errors).length}
                  >
                    Generate {environment.toUpperCase()} Stripe Connect Link
                  </button>
                )}
                
                {connectionType === 'existing' && (
                  <button
                    type="button"
                    className="submit-btn"
                    onClick={handleSelectExistingAccount}
                    disabled={!selectedExistingAccount || !!Object.keys(errors).length}
                  >
                    Continue with Selected Account
                  </button>
                )}
              </>
            ) : showConnectUrl ? (
              <button
                type="button"
                className="submit-btn"
                onClick={onCancel}
              >
                Done
              </button>
            ) : (
              <>
                <button
                  type="button"
                  className="submit-btn"
                  onClick={handleConnectExistingAccount}
                  disabled={isValidating}
                >
                  {isValidating ? 'Validating...' : `Confirm ${environment.toUpperCase()} Connection`}
                </button>
                <button
                  type="button"
                  className="secondary-btn"
                  onClick={() => setShowConfirmation(false)}
                >
                  Go Back
                </button>
              </>
            )}
            
            {!showConnectUrl && (
              <button
                type="button"
                className="cancel-btn"
                onClick={onCancel}
              >
                Cancel
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StripeConnectionModal;